import React, { Component } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import { Table, Modal } from 'react-bootstrap';
import { injectIntl } from 'react-intl';
import cx from 'classnames';
import moment from 'moment';

import s from './DriverEarningsList.css';
import { bookingStatusLocale } from '../../../../helpers/bookingStatusLocale';
import CurrencyConverter from '../../../CurrencyConverter';
import CustomPagination from '../../../CustomPagination';
import Link from '../../../Link';
import { getAvatarUrl, getUiAvatarUrl } from '../../../../utils/getAvatarUrl';

const ALL_STATUSES = ['completed', 'failed', 'deliveryNotFound'];

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
];

// Maps to the resolver's `bookingType` arg. '' means no filter — the resolver
// treats a missing value as 'all'.
const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'food', label: 'Food' },
  { value: 'grocery', label: 'Grocery' },
  { value: 'ride', label: 'Ride' },
];

const RESULT_TYPE_LABELS = {
  food: 'Food',
  grocery: 'Grocery',
  ride: 'Ride',
};

const DATE_PRESET_OPTIONS = [
  { value: '', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'custom', label: 'Custom Range' },
];

class DriverEarningsList extends Component {
  static propTypes = {
    earningsData: PropTypes.object,
    driverId: PropTypes.string.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      currentPage: 1,
      searchQuery: '',
      showFilterModal: false,
      // Apollo's `loading` prop is not used for refetches here: it would unmount
      // this component (see DriverEarnings.js) and wipe the filter state. This
      // flag drives an in-place overlay instead.
      isRefetching: false,
      // Applied filters
      startDate: '',
      endDate: '',
      selectedStatus: '',
      selectedType: '',
      datePreset: '',
      // Draft filters (inside modal before Apply)
      draftStartDate: '',
      draftEndDate: '',
      draftStatus: '',
      draftType: '',
      draftDatePreset: '',
    };
    this.runRefetch = this.runRefetch.bind(this);
    this.paginationData = this.paginationData.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.openFilterModal = this.openFilterModal.bind(this);
    this.closeFilterModal = this.closeFilterModal.bind(this);
    this.applyFilters = this.applyFilters.bind(this);
    this.clearDraftFilters = this.clearDraftFilters.bind(this);
    this.selectDatePreset = this.selectDatePreset.bind(this);
  }

  componentWillUnmount() {
    this._isUnmounted = true;
    clearTimeout(this._searchTimeout);
  }

  // Single entry point for every refetch so the overlay can never be left on:
  // the flag is cleared whether the query resolves or rejects.
  runRefetch(variables) {
    const { earningsData: { refetch } } = this.props;
    this.setState({ isRefetching: true });
    return refetch(variables)
      .catch(() => { /* the error surfaces through earningsData */ })
      .then(() => {
        if (!this._isUnmounted) this.setState({ isRefetching: false });
      });
  }

  getStatusesArg(selectedStatus) {
    if (!selectedStatus) return ALL_STATUSES;
    if (selectedStatus === 'failed') return ['failed', 'deliveryNotFound'];
    return [selectedStatus];
  }

  triggerRefetch(overrides = {}) {
    const { driverId } = this.props;
    const { currentPage, startDate, endDate, selectedStatus, selectedType, searchQuery } = this.state;
    return this.runRefetch({
      driverId,
      currentPage,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      statuses: this.getStatusesArg(selectedStatus),
      bookingType: selectedType || undefined,
      orderId: searchQuery ? parseInt(searchQuery, 10) : undefined,
      ...overrides,
    });
  }

  paginationData(currentPage) {
    this.setState({ currentPage });
    this.triggerRefetch({ currentPage });
  }

  handleSearchChange(e) {
    const searchQuery = e.target.value.replace(/\D/g, '');
    this.setState({ searchQuery });
    clearTimeout(this._searchTimeout);
    this._searchTimeout = setTimeout(() => {
      this.setState({ currentPage: 1 });
      this.triggerRefetch({
        currentPage: 1,
        orderId: searchQuery ? parseInt(searchQuery, 10) : undefined,
      });
    }, 400);
  }

  selectDatePreset(preset) {
    if (preset === '' || preset === 'custom') {
      this.setState({ draftDatePreset: preset, draftStartDate: '', draftEndDate: '' });
      return;
    }
    const today = moment().format('YYYY-MM-DD');
    let start = today;
    if (preset === 'week') start = moment().startOf('isoWeek').format('YYYY-MM-DD');
    if (preset === 'month') start = moment().startOf('month').format('YYYY-MM-DD');
    this.setState({ draftDatePreset: preset, draftStartDate: start, draftEndDate: today });
  }

  openFilterModal() {
    const { startDate, endDate, selectedStatus, selectedType, datePreset } = this.state;
    this.setState({
      showFilterModal: true,
      draftStartDate: startDate,
      draftEndDate: endDate,
      draftStatus: selectedStatus,
      draftType: selectedType,
      draftDatePreset: datePreset,
    });
  }

  closeFilterModal() {
    this.setState({ showFilterModal: false });
  }

  applyFilters() {
    const { driverId } = this.props;
    const { draftStartDate, draftEndDate, draftStatus, draftType, draftDatePreset, searchQuery } = this.state;
    this.setState({
      startDate: draftStartDate,
      endDate: draftEndDate,
      selectedStatus: draftStatus,
      selectedType: draftType,
      datePreset: draftDatePreset,
      currentPage: 1,
      showFilterModal: false,
    });
    this.runRefetch({
      driverId,
      currentPage: 1,
      startDate: draftStartDate || undefined,
      endDate: draftEndDate || undefined,
      statuses: this.getStatusesArg(draftStatus),
      bookingType: draftType || undefined,
      orderId: searchQuery ? parseInt(searchQuery, 10) : undefined,
    });
  }

  clearDraftFilters() {
    this.setState({
      draftStartDate: '',
      draftEndDate: '',
      draftStatus: '',
      draftType: '',
      draftDatePreset: '',
    });
  }

  getActiveFilterCount() {
    const { startDate, endDate, selectedStatus, selectedType } = this.state;
    let count = 0;
    if (startDate || endDate) count++;
    if (selectedStatus) count++;
    if (selectedType) count++;
    return count;
  }

  getStatusBadgeClass(status) {
    if (status === 'completed') return s.badgeSuccess;
    if (status === 'failed' || status === 'deliveryNotFound') return s.badgeDanger;
    return s.badgeDefault;
  }

  getTypeBadgeClass(bookingType) {
    if (bookingType === 'ride') return s.badgeRide;
    if (bookingType === 'grocery') return s.badgeGrocery;
    if (bookingType === 'food') return s.badgeFood;
    return s.badgeDefault;
  }

  getPaymentBadgeClass(paymentType) {
    if (paymentType === 1) return s.badgeCash;
    if (paymentType === 2) return s.badgeCard;
    if (paymentType === 3 || paymentType === 4) return s.badgeWallet;
    return s.badgeDefault;
  }

  renderPaymentLabel(paymentType) {
    if (paymentType === 1) return 'Cash';
    if (paymentType === 2) return 'Card';
    if (paymentType === 3 || paymentType === 4) return 'QRIS';
    return '-';
  }

  render() {
    const { earningsData: { getDriverEarningsAdmin } } = this.props;
    const { locale } = this.props.intl;
    const {
      currentPage, searchQuery, showFilterModal, isRefetching,
      draftStartDate, draftEndDate, draftStatus, draftType, draftDatePreset,
    } = this.state;

    const data = getDriverEarningsAdmin;
    const driverProfile = data?.driverProfile;
    const summary = data?.summary;
    const results = data?.results || [];
    const count = data?.count || 0;
    const activeFilterCount = this.getActiveFilterCount();

    return (
      <div className={s.pageContent}>
        <Link to="/siteadmin/drivers" className={s.backLink}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to Drivers
        </Link>

        <div className={s.sectionProfile}>
          {driverProfile && (
            <div className={s.driverHeader}>
              <img
                src={
                  driverProfile.picture
                    ? getAvatarUrl(driverProfile.picture)
                    : getUiAvatarUrl(`${driverProfile.firstName} ${driverProfile.lastName}`)
                }
                className={s.driverAvatar}
                alt="Driver"
              />
              <div className={s.driverInfo}>
                <div className={s.driverName}>
                  {driverProfile.firstName} {driverProfile.lastName}
                </div>
                <div className={s.driverContact}>
                  {driverProfile.phoneDialCode} {driverProfile.phoneNumber}
                </div>
              </div>
            </div>
          )}

          {summary && (
            <div className={s.summaryCards}>
            <div className={s.summaryCard}>
              <div className={s.summaryCardHeader}>
                <div className={s.summaryCardLabel}>Total Orders</div>
                <div className={cx(s.summaryCardIcon, s.summaryCardIconOrders)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" /><path d="M16 10a4 4 0 0 1-8 0" />
                  </svg>
                </div>
              </div>
              <div className={s.summaryCardValue}>{summary.totalOrders}</div>
            </div>
              <div className={s.summaryCard}>
              <div className={s.summaryCardHeader}>
                <div className={s.summaryCardLabel}>Total Earnings</div>
                <div className={cx(s.summaryCardIcon, s.summaryCardIconEarnings)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                  </svg>
                </div>
              </div>
              <div className={s.summaryCardValue}>
                <CurrencyConverter from={summary.currency} amount={summary.totalEarnings} />
              </div>
            </div>
            <div className={s.summaryCard}>
              <div className={s.summaryCardHeader}>
                <div className={s.summaryCardLabel}>Setoran</div>
                <div className={cx(s.summaryCardIcon, s.summaryCardIconDeposit)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19" /><polyline points="19 12 12 19 5 12" />
                  </svg>
                </div>
              </div>
              <div className={s.summaryCardValue}>
                <CurrencyConverter from={summary.currency} amount={summary.totalDeposit} />
              </div>
            </div>
            <div className={s.summaryCard}>
              <div className={s.summaryCardHeader}>
                <div className={s.summaryCardLabel}>Reimbursement</div>
                <div className={cx(s.summaryCardIcon, s.summaryCardIconReimbursement)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 .49-3.17" />
                  </svg>
                </div>
              </div>
              <div className={s.summaryCardValue}>
                <CurrencyConverter from={summary.currency} amount={summary.totalReimbursement} />
              </div>
            </div>
          </div>
          )}
        </div>

        <div className={s.sectionOrders}>
        <div className={s.toolbar}>
          <div className={s.searchWrapper}>
            <span className={s.searchIcon}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </span>
            <input
              type="text"
              className={s.searchInput}
              placeholder="Search by Order ID..."
              value={searchQuery}
              onChange={this.handleSearchChange}
              maxLength={12}
            />
            {searchQuery && (
              <button
                className={s.searchClear}
                onClick={() => {
                  this.setState({ searchQuery: '', currentPage: 1 });
                  clearTimeout(this._searchTimeout);
                  this.triggerRefetch({ currentPage: 1, orderId: undefined });
                }}
              >
                ×
              </button>
            )}
          </div>
          <button className={s.filterBtn} onClick={this.openFilterModal}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            <span>Filter</span>
            {activeFilterCount > 0 && (
              <span className={s.filterBadge}>{activeFilterCount}</span>
            )}
          </button>
        </div>

        <div className={s.resultsArea}>
        {isRefetching && (
          <div className={s.resultsOverlay}>
            <span className={s.spinner} />
          </div>
        )}
        <div className={cx('tableCss', 'tableSticky', 'NewAdminResponsiveTable', 'topScrollbarTable')}>
          <Table className={cx('table', 'topScrollbarTableContent')}>
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Type</th>
                <th scope="col">Order ID</th>
                <th scope="col">Pickup</th>
                <th scope="col">Dropoff</th>
                <th scope="col">Payment</th>
                <th scope="col">Status</th>
                <th scope="col">Driver Earnings</th>
              </tr>
            </thead>
            <tbody>
              {results.length > 0 && results.map((item, index) => (
                // id alone collides across the Booking / RideBooking tables
                <tr key={`${item.bookingType}-${item.id}-${index}`}>
                  <td data-label="Date">
                    {moment(item.createdAt).format('DD-MM-YYYY HH:mm')}
                  </td>
                  <td data-label="Type">
                    <span className={cx(s.badge, this.getTypeBadgeClass(item.bookingType))}>
                      {RESULT_TYPE_LABELS[item.bookingType] || '-'}
                    </span>
                  </td>
                  <td data-label="Order ID">
                    {/* A ride has no Orders row, so its own RideBooking id is
                        the reference. Not linked: /siteadmin/bookings expects a
                        Booking id and the two sequences collide. */}
                    {item.bookingType === 'ride' ? (
                      <span className={s.idNoLink}>#{item.id}</span>
                    ) : (
                      <Link to={`/siteadmin/bookings/view/${item.id}`}>
                        #{item.orderId}
                      </Link>
                    )}
                  </td>
                  <td data-label="Pickup" className={s.locationCell} title={item.pickUpLocation}>
                    {item.pickUpLocation || '-'}
                  </td>
                  <td data-label="Dropoff" className={s.locationCell} title={item.dropOffLocation}>
                    {item.dropOffLocation || '-'}
                  </td>
                  <td data-label="Payment">
                    <span className={cx(s.badge, this.getPaymentBadgeClass(item.paymentType))}>
                      {this.renderPaymentLabel(item.paymentType)}
                    </span>
                  </td>
                  <td data-label="Status">
                    <span className={cx(s.badge, this.getStatusBadgeClass(item.bookingStatus))}>
                      {bookingStatusLocale(item.bookingStatus, locale)}
                    </span>
                  </td>
                  <td data-label="Driver Earnings" className={s.earningsCell}>
                    <CurrencyConverter from={item.currency} amount={item.driverTotalFare} />
                  </td>
                </tr>
              ))}
              {results.length === 0 && (
                <tr>
                  <td colSpan={8}>
                    <div className={s.emptyState}>
                      <div className={s.emptyIcon}>
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
                        </svg>
                      </div>
                      <div className={s.emptyTitle}>No orders found</div>
                      <div className={s.emptySubtitle}>Try adjusting your search or filters</div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {count > 0 && (
          <div className={cx('space5', 'spaceTop5')}>
            <CustomPagination
              total={count}
              currentPage={currentPage}
              defaultCurrent={1}
              defaultPageSize={20}
              change={this.paginationData}
              paginationLabel="orders"
            />
          </div>
        )}
        </div>
        </div>

        <Modal show={showFilterModal} onHide={this.closeFilterModal} centered>
          <Modal.Header closeButton className={s.modalHeader}>
            <Modal.Title className={s.modalTitle}>Filter Orders</Modal.Title>
          </Modal.Header>
          <Modal.Body className={s.modalBody}>
            <div className={s.modalSection}>
              <div className={s.modalSectionTitle}>Date Range</div>
              <div className={s.filterSelectWrapper}>
                <select
                  className={s.filterSelect}
                  value={draftDatePreset}
                  onChange={(e) => this.selectDatePreset(e.target.value)}
                >
                  {DATE_PRESET_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <span className={s.filterSelectArrow}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </span>
              </div>
              {draftDatePreset === 'custom' && (
                <div className={s.dateRangeRow}>
                  <div className={s.dateField}>
                    <label className={s.modalLabel}>Start Date</label>
                    <input
                      type="date"
                      className={s.filterInput}
                      value={draftStartDate}
                      onChange={(e) => this.setState({ draftStartDate: e.target.value })}
                    />
                  </div>
                  <div className={s.dateField}>
                    <label className={s.modalLabel}>End Date</label>
                    <input
                      type="date"
                      className={s.filterInput}
                      value={draftEndDate}
                      onChange={(e) => this.setState({ draftEndDate: e.target.value })}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className={s.modalSection}>
              <div className={s.modalSectionTitle}>Order Type</div>
              <div className={s.filterSelectWrapper}>
                <select
                  className={s.filterSelect}
                  value={draftType}
                  onChange={(e) => this.setState({ draftType: e.target.value })}
                >
                  {TYPE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <span className={s.filterSelectArrow}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </span>
              </div>
            </div>

            <div className={s.modalSection}>
              <div className={s.modalSectionTitle}>Status</div>
              <div className={s.filterSelectWrapper}>
                <select
                  className={s.filterSelect}
                  value={draftStatus}
                  onChange={(e) => this.setState({ draftStatus: e.target.value })}
                >
                  {STATUS_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <span className={s.filterSelectArrow}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </span>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className={s.modalFooter}>
            <button className={s.clearBtn} onClick={this.clearDraftFilters}>
              Clear All
            </button>
            <button className={s.applyBtn} onClick={this.applyFilters} disabled={isRefetching}>
              {isRefetching ? 'Applying…' : 'Apply Filters'}
            </button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}

export default injectIntl(withStyles(s)(DriverEarningsList));
